import { useEffect, useState, useCallback } from "react";
import { ShieldCheck, Clock, ArrowLeft, XCircle, Building2, MapPin, Layers } from "lucide-react";
import { api } from "@/lib/api";

const fmt = (s) => {
  if (s == null) return "--:--";
  const m = Math.floor(s / 60), r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
};

export default function SupportBanner() {
  const [s, setS] = useState(null);        // support session state
  const [loc, setLoc] = useState(null);    // location context (multi)
  const [remaining, setRemaining] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/support/session");
      if (!data.active) {
        if (s?.active) window.location.href = "/support?ended=1";
        setS({ active: false });
        return;
      }
      setS(data);
      setRemaining(data.remaining_seconds);
      if (data.account_mode === "multi") {
        try { const { data: lc } = await api.get("/me/locations"); setLoc(lc); } catch { /* ignore */ }
      }
    } catch { setS({ active: false }); }
  }, [s?.active]);

  useEffect(() => { refresh(); const i = setInterval(refresh, 15000); return () => clearInterval(i); }, [refresh]);
  useEffect(() => {
    if (remaining == null) return;
    const t = setInterval(() => setRemaining((r) => {
      if (r == null) return r;
      if (r <= 1) { window.location.href = "/support?ended=1"; return 0; }
      return r - 1;
    }), 1000);
    return () => clearInterval(t);
  }, [remaining != null]);

  const end = async (backToControl) => {
    try { await api.post("/support/session/end", {}); } catch { /* ignore */ }
    if (backToControl) { try { window.close(); } catch { /* ignore */ } }
    window.location.href = "/support?ended=1";
  };

  if (!s || !s.active) return null;

  const isMulti = s.account_mode === "multi";
  const activeLoc = loc?.locations?.find((l) => l.id === loc?.active_location_id);
  const locLabel = loc ? (loc.active_location_id ? (activeLoc?.name || "Standort") : "Alle Standorte") : null;

  return (
    <div className="sticky top-0 z-[60] flex flex-wrap items-center gap-x-5 gap-y-2 border-b-2 px-4 py-2.5 sm:px-6"
      style={{ background: "#2C2E33", borderBottomColor: "#D89A00" }} data-testid="support-banner">
      <span className="flex items-center gap-2 text-[12px] font-black uppercase tracking-[0.14em] text-fundio-gold">
        <ShieldCheck size={16} /> Super-Admin Ansicht
      </span>
      <span className="flex items-center gap-1.5 text-[13px] font-semibold text-white" data-testid="support-org">
        <Building2 size={14} className="opacity-70" /> {s.company_name}
      </span>
      <span className="rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-bold text-white" data-testid="support-plan">{s.plan}</span>
      <span className="rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-bold text-white" data-testid="support-mode">
        {isMulti ? "MULTI" : "Single"}
      </span>
      {isMulti && locLabel && (
        <span className="flex items-center gap-1.5 text-[12.5px] text-white/85" data-testid="support-location">
          {loc?.active_location_id ? <MapPin size={13} /> : <Layers size={13} />} {locLabel}
        </span>
      )}
      <span className="ml-auto flex items-center gap-1.5 font-mono text-[13px] font-bold text-white" data-testid="support-remaining">
        <Clock size={14} className="text-fundio-gold" /> {fmt(remaining)}
      </span>
      <button onClick={() => end(true)} data-testid="support-back-to-control"
        className="flex items-center gap-1.5 rounded-lg border border-white/25 px-3 py-1.5 text-[12.5px] font-semibold text-white hover:bg-white/10">
        <ArrowLeft size={14} /> Zurück zu Control
      </button>
      <button onClick={() => end(false)} data-testid="support-end-view"
        className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12.5px] font-bold text-fundio-black" style={{ background: "#D89A00" }}>
        <XCircle size={14} /> Ansicht beenden
      </button>
    </div>
  );
}
